<template>
    <div v-if="visible" ref="applicationOverlay" class="photographer-application-overlay" @click.self="closeDialog">
        <section ref="applicationDialog" tabindex="-1" class="photographer-application-dialog" role="dialog" aria-modal="true" aria-labelledby="photographerApplicationTitle">
            <header class="photographer-application-header">
                <div>
                    <p>INITIAL APPLICATION</p>
                    <h2 id="photographerApplicationTitle">申请成为摄影师</h2>
                    <span>申请将复用当前实名状态，无需重复上传身份证或学生证照片；其余材料仅用于本次审核。</span>
                </div>
                <button type="button" class="photographer-dialog-close" aria-label="关闭申请表" @click="closeDialog">×</button>
            </header>

            <form class="photographer-application-form" novalidate @submit.prevent="submitApplication">
                <main class="photographer-application-main">
                    <p v-if="dataLoading" class="photographer-loading-copy">正在加载可选摄影风格…</p>
                    <p v-else-if="loadError" class="photographer-form-error" role="alert">{{ loadError }}</p>

                    <section class="photographer-form-section">
                        <div class="photographer-section-heading">
                            <span>01</span>
                            <div><h3>基础资料</h3><p>联系电话仅属于摄影师资料，验证码使用登录短信验证码。</p></div>
                        </div>
                        <div class="photographer-form-grid">
                            <role-portrait-upload :key="applicationEpoch" class="photographer-field-full" v-model="form.personalPhoto" required @change="clearErrors" @error="setClientError($event, 'personalPhoto')" />
                            <label><span>摄影师联系电话 <b>*</b></span><input v-model.trim="form.phone" inputmode="numeric" maxlength="11" placeholder="请输入中国大陆手机号" /></label>
                            <label><span>短信验证码 <b>*</b></span><span class="photographer-code-input"><input v-model.trim="form.smsCode" inputmode="numeric" maxlength="6" placeholder="6 位验证码" /><button type="button" :disabled="smsSending || smsCooldown > 0" @click="requestSmsCode">{{ smsButtonText }}</button></span></label>
                            <label><span>省份 <b>*</b></span><div class="photographer-theme-selector"><button type="button" class="photographer-theme-trigger" :class="{ 'is-placeholder': !form.provinceCode }" :aria-expanded="isSelectOpen('province')" aria-haspopup="listbox" @click="toggleSelect('province')">{{ selectedOptionLabel(provinceOptions, form.provinceCode, '请选择省份') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('province')" class="photographer-theme-menu" role="listbox" aria-label="省份"><button v-for="item in provinceOptions" :key="item.value" type="button" role="option" :aria-selected="form.provinceCode === item.value" :class="{ active: form.provinceCode === item.value }" @click="selectProvince(item.value)">{{ item.label }}<i v-if="form.provinceCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label><span>城市 <b>*</b></span><div class="photographer-theme-selector"><button type="button" class="photographer-theme-trigger" :class="{ 'is-placeholder': !form.cityCode }" :disabled="!form.provinceCode" :aria-expanded="isSelectOpen('city')" aria-haspopup="listbox" @click="toggleSelect('city')">{{ selectedOptionLabel(cityOptions, form.cityCode, '请选择城市') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('city')" class="photographer-theme-menu" role="listbox" aria-label="城市"><button v-for="item in cityOptions" :key="item.value" type="button" role="option" :aria-selected="form.cityCode === item.value" :class="{ active: form.cityCode === item.value }" @click="selectCity(item.value)">{{ item.label }}<i v-if="form.cityCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label class="photographer-field-full"><span>区 / 县 <b>*</b></span><div class="photographer-theme-selector"><button type="button" class="photographer-theme-trigger" :class="{ 'is-placeholder': !form.districtCode }" :disabled="!form.cityCode" :aria-expanded="isSelectOpen('district')" aria-haspopup="listbox" @click="toggleSelect('district')">{{ selectedOptionLabel(districtOptions, form.districtCode, '请选择区 / 县') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('district')" class="photographer-theme-menu" role="listbox" aria-label="区或县"><button v-for="item in districtOptions" :key="item.value" type="button" role="option" :aria-selected="form.districtCode === item.value" :class="{ active: form.districtCode === item.value }" @click="selectDistrict(item.value)">{{ item.label }}<i v-if="form.districtCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label class="photographer-field-full"><span>专业简介 <b>*</b></span><textarea v-model="form.professionalIntro" maxlength="2000" rows="4" placeholder="介绍拍摄经验、服务范围与擅长的影像表达。"></textarea><small>{{ professionalIntroLength }}/2000</small></label>
                        </div>
                    </section>



                    <section class="photographer-form-section">
                        <div class="photographer-section-heading photographer-section-heading--action">
                            <div class="photographer-section-number-copy"><span>02</span><div><h3>摄影风格与样片</h3><p>每个风格依次选择摄影主题和具体风格，并上传恰好 5 张原创样片。</p></div></div>
                            <button type="button" class="photographer-add-button" @click="addStyle">新增风格</button>
                        </div>
                        <p v-if="!dataLoading && !themeOptions.length" class="photographer-empty-copy">当前没有可申请的摄影主题／风格，请稍后重试。</p>
                        <article v-for="(style, index) in form.styles" :key="style.localId" class="photographer-repeat-card">
                            <div class="photographer-repeat-card-head"><strong>风格 {{ index + 1 }}</strong><button v-if="form.styles.length > 1" type="button" @click="removeStyle(index)">移除</button></div>
                            <div class="photographer-form-grid photographer-style-grid">
                                <div class="photographer-theme-field">
                                    <span>摄影主题 <b>*</b></span>
                                    <div class="photographer-theme-selector">
                                        <button
                                            type="button"
                                            class="photographer-theme-trigger"
                                            :class="{ 'is-placeholder': !style.themeId }"
                                            :disabled="dataLoading || !themeOptions.length"
                                            :aria-expanded="isSelectOpen(`theme-${style.localId}`)"
                                            aria-haspopup="listbox"
                                            @click="toggleSelect(`theme-${style.localId}`)">{{ selectedThemeName(style) }}<i aria-hidden="true">⌄</i></button>
                                        <div v-if="isSelectOpen(`theme-${style.localId}`)" class="photographer-theme-menu" role="listbox" aria-label="摄影主题">
                                            <button
                                                v-for="theme in themeOptions"
                                                :key="theme.themeId"
                                                type="button"
                                                role="option"
                                                :aria-selected="style.themeId === theme.themeId"
                                                :class="{ active: style.themeId === theme.themeId }"
                                                @click="selectThemeOption(style, theme)">{{ theme.themeName }}<i v-if="style.themeId === theme.themeId" aria-hidden="true">✓</i></button>
                                        </div>
                                    </div>
                                </div>
                                <label><span>具体风格 <b>*</b></span><div class="photographer-theme-selector"><button type="button" class="photographer-theme-trigger" :class="{ 'is-placeholder': !style.styleId }" :disabled="!style.themeId" :aria-expanded="isSelectOpen(`style-${style.localId}`)" aria-haspopup="listbox" @click="toggleSelect(`style-${style.localId}`)">{{ selectedStyleName(style) }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen(`style-${style.localId}`)" class="photographer-theme-menu" role="listbox" aria-label="具体风格"><button v-for="styleOption in styleOptionsFor(style)" :key="styleOption.styleId" type="button" role="option" :aria-selected="style.styleId === styleOption.styleId" :class="{ active: style.styleId === styleOption.styleId }" @click="selectStyleOption(style, styleOption)">{{ styleOption.styleName }}<i v-if="style.styleId === styleOption.styleId" aria-hidden="true">✓</i></button></div></div></label>
                            </div>
                            <professional-application-image-upload
                                label="原创样片"
                                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                required
                                :max-count="5"
                                :files="style.samples"
                                :preview-urls="style.samplePreviewUrls"
                                hint="请逐张上传 JPG / PNG 图片；每个风格必须上传 5 张。"
                                @select="selectStyleSamples(style, $event)"
                                @remove="removeStyleSample(style, $event)" />
                        </article>
                    </section>

                    <section class="photographer-form-section">
                        <div class="photographer-section-heading">
                            <span>03</span>
                            <div><h3>设备与证书 <small>可选</small></h3><p>填写设备或证书名称后，必须同时上传对应证明材料。</p></div>
                        </div>
                        <div class="photographer-dual-section">
                            <section>
                                <div class="photographer-subheading"><strong>设备</strong><button type="button" class="photographer-add-button" @click="addEquipment">新增设备</button></div>
                                <article v-for="(equipment, index) in form.equipments" :key="equipment.localId" class="photographer-repeat-card photographer-repeat-card--compact">
                                    <button class="photographer-remove-circle" type="button" aria-label="移除设备" @click="removeEquipment(index)">×</button>
                                    <label>设备类型<input v-model.trim="equipment.equipmentType" maxlength="100" placeholder="例如：相机" /></label>
                                    <label>品牌型号<input v-model.trim="equipment.brandModel" maxlength="255" placeholder="例如：Sony A7M4" /></label>
                                    <professional-application-image-upload
                                    label="设备证明图"
                                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                    :max-count="1"
                                    :files="equipment.proofImages"
                                    :preview-urls="equipment.proofPreviewUrls"
                                    hint="支持 JPG / PNG，单张最大 5 MiB；已填写设备时至少上传 1 张。"
                                        @select="selectEquipmentProof(equipment, $event)"
                                        @remove="removeEquipmentProof(equipment, $event)" />
                                </article>
                            </section>
                            <section>
                                <div class="photographer-subheading"><strong>证书</strong><button type="button" class="photographer-add-button" @click="addCertificate">新增证书</button></div>
                                <article v-for="(certificate, index) in form.certificates" :key="certificate.localId" class="photographer-repeat-card photographer-repeat-card--compact">
                                    <button class="photographer-remove-circle" type="button" aria-label="移除证书" @click="removeCertificate(index)">×</button>
                                    <label>证书名称<input v-model.trim="certificate.name" maxlength="255" placeholder="例如：摄影协会会员证" /></label>
                                    <professional-application-file-field
                                        label="证书原件"
                                        accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                                        hint="支持 JPG / PNG / PDF，单个最大 5 MiB"
                                        :file="certificate.file"
                                        @select="selectCertificateFile(certificate, $event)"
                                        @remove="certificate.file = null; clearErrors()" />
                                </article>
                            </section>
                        </div>
                    </section>

                    <application-agreement-panel ref="agreementPanel" :state="agreement" :title="agreementTitle" :disabled="submitting"
                        @read="readAgreement" @toggle="toggleAgreement" @agree="acceptAgreement" @exit="exitAgreement" @retry="readAgreement" @back="returnToAgreement" />

                    <div v-if="formError || fieldErrors.length" class="photographer-form-error" role="alert">
                        <p v-if="formError">{{ formError }}</p>
                        <ul v-if="fieldErrors.length"><li v-for="(fieldError, index) in fieldErrors" :key="`${fieldError.field}-${index}`"><strong>{{ fieldLabel(fieldError.field) }}</strong>：{{ fieldError.message }}</li></ul>
                    </div>
                </main>

                <footer class="photographer-dialog-footer">
                    <span>提交前请确认材料真实、完整</span>
                    <div><button type="button" class="photographer-secondary-button" :disabled="submitting" @click="closeDialog">取消</button><button type="submit" class="photographer-submit-button" :disabled="submitting || dataLoading || !!loadError">{{ submitting ? '正在提交…' : '提交首次申请' }}</button></div>
                </footer>
            </form>
        </section>
    </div>
</template>

<script>
import qs from "qs";
import applicationAgreement from "./applicationAgreement";
import ApplicationAgreementPanel from "./ApplicationAgreementPanel";
import { regionData } from "element-china-area-data";
import request from "@/tool/request";
import API from "@/tool/api";
import developmentSmsCode from "@/tool/developmentSmsCode.cjs";
import RolePortraitUpload from "./RolePortraitUpload";
import ProfessionalApplicationFileField from "./ProfessionalApplicationFileField";
import ProfessionalApplicationImageUpload from "./ProfessionalApplicationImageUpload";
import {
    appendUploadFile,
    removeUploadFile,
} from "@/tool/professionalApplicationUpload.cjs";
import {
    getResultData,
    getResultMessage,
    getResultPayload,
    isResultSuccess,
} from "@/tool/result";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_EQUIPMENT_PROOF_COUNT = 1;
const IMAGE_TYPES = ["image/jpeg", "image/png"];
let itemSequence = 0;

const nextItemId = (prefix) => `${prefix}-${itemSequence += 1}`;
const createStyle = () => ({
    localId: nextItemId("style"),
    themeId: "",
    styleId: "",
    themeStyleId: "",
    samples: [],
    samplePreviewUrls: [],
});
const createEquipment = () => ({
    localId: nextItemId("equipment"),
    equipmentType: "",
    brandModel: "",
    proofImages: [],
    proofPreviewUrls: [],
});
const createCertificate = () => ({
    localId: nextItemId("certificate"),
    name: "",
    file: null,
});
const asText = (value) => typeof value === "string" ? value.trim() : "";

export default {
    name: "PhotographerApplicationDialog",
    mixins: [applicationAgreement(1)],
    components: {
        ApplicationAgreementPanel,
        RolePortraitUpload,
        ProfessionalApplicationFileField,
        ProfessionalApplicationImageUpload,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dataLoading: false,
            loadError: "",
            submitting: false,
            smsSending: false,
            smsCooldown: 0,
            smsTimer: null,
            themeOptions: [],
            formError: "",
            fieldErrors: [],
            openSelectId: null,
            form: this.createEmptyForm(),
        };
    },
    computed: {
        provinceOptions() {
            return Array.isArray(regionData) ? regionData : [];
        },
        selectedProvince() {
            return this.findOption(this.provinceOptions, this.form.provinceCode);
        },
        cityOptions() {
            return this.selectedProvince && Array.isArray(this.selectedProvince.children)
                ? this.selectedProvince.children
                : [];
        },
        selectedCity() {
            return this.findOption(this.cityOptions, this.form.cityCode);
        },
        districtOptions() {
            return this.selectedCity && Array.isArray(this.selectedCity.children)
                ? this.selectedCity.children
                : [];
        },
        selectedDistrict() {
            return this.findOption(this.districtOptions, this.form.districtCode);
        },
        professionalIntroLength() {
            return this.form.professionalIntro.length;
        },
        smsButtonText() {
            if (this.smsSending) return "发送中…";
            return this.smsCooldown > 0 ? `${this.smsCooldown}s 后重试` : "获取验证码";
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(isVisible) {
                if (isVisible) {
                    this.openDialog();
                }
            },
        },
    },
    beforeDestroy() {
        this.stopSmsTimer();
        this.revokeAllSamplePreviews();
        document.removeEventListener("click", this.handleDocumentClick);
    },
    mounted() {
        document.addEventListener("click", this.handleDocumentClick);
    },
    methods: {
        createEmptyForm() {
            return {
                phone: "",
                smsCode: "",
                provinceCode: "",
                cityCode: "",
                districtCode: "",
                professionalIntro: "",
                personalPhoto: null,
                styles: [createStyle()],
                equipments: [],
                certificates: [],
            };
        },
        openDialog() {
            this.invalidateApplication();
            this.stopSmsTimer();
            this.revokeAllSamplePreviews();
            this.form = this.createEmptyForm();
            this.formError = "";
            this.fieldErrors = [];
            this.loadError = "";
            this.themeOptions = [];
            this.loadApplicationDependencies();
        },
        loadApplicationDependencies() {
            const context = this.applicationContext();
            this.dataLoading = true;
            request.get(API.creatorCenter.photographer.styleOptions).then((styleResponse) => {
                if (!this.applicationIsCurrent(context)) return;
                if (!isResultSuccess(styleResponse)) throw styleResponse;
                const styleData = getResultData(styleResponse) || {};
                this.themeOptions = Array.isArray(styleData.themes) ? styleData.themes : [];
            }).catch((error) => {
                if (!this.applicationIsCurrent(context)) return;
                this.loadError = getResultMessage(error, "申请所需的摄影风格信息加载失败，请关闭后重试");
            }).finally(() => {
                if (this.applicationIsCurrent(context)) this.dataLoading = false;
            });
        },
        closeDialog() {
            if (!this.submitting) {
                this.invalidateApplication();
                this.$emit("close");
            }
        },
        findOption(options, value) {
            return (options || []).find((item) => String(item.value) === String(value)) || null;
        },
        handleProvinceChange() {
            this.form.cityCode = "";
            this.form.districtCode = "";
        },
        handleCityChange() {
            this.form.districtCode = "";
        },
        styleOptionsFor(style) {
            const theme = (this.themeOptions || []).find((item) => String(item.themeId) === String(style.themeId));
            return theme && Array.isArray(theme.styles) ? theme.styles : [];
        },
        selectedOptionLabel(options, value, placeholder) {
            const option = (options || []).find((item) => String(item.value) === String(value));
            return option ? option.label : placeholder;
        },
        selectedThemeName(style) {
            const theme = (this.themeOptions || []).find((item) => String(item.themeId) === String(style.themeId));
            return theme ? theme.themeName : "请选择主题";
        },
        selectedStyleName(style) {
            const styleOption = this.styleOptionsFor(style).find((item) => String(item.styleId) === String(style.styleId));
            return styleOption ? styleOption.styleName : "请选择具体风格";
        },
        isSelectOpen(selectId) {
            return this.openSelectId === selectId;
        },
        toggleSelect(selectId) {
            this.openSelectId = this.isSelectOpen(selectId) ? null : selectId;
        },
        selectProvince(value) {
            this.form.provinceCode = value;
            this.handleProvinceChange();
            this.openSelectId = null;
        },
        selectCity(value) {
            this.form.cityCode = value;
            this.handleCityChange();
            this.openSelectId = null;
        },
        selectDistrict(value) {
            this.form.districtCode = value;
            this.openSelectId = null;
        },
        selectThemeOption(style, theme) {
            style.themeId = theme.themeId;
            this.resetStyleSelection(style);
            this.openSelectId = null;
        },
        selectStyleOption(style, styleOption) {
            style.styleId = styleOption.styleId;
            style.themeStyleId = styleOption.themeStyleId;
            this.openSelectId = null;
        },
        handleDocumentClick(event) {
            const target = event && event.target;
            if (!target || !target.closest || !target.closest(".photographer-theme-selector")) {
                this.openSelectId = null;
            }
        },
        resetStyleSelection(style) {
            style.styleId = "";
            style.themeStyleId = "";
        },
        setClientError(message, field = "") {
            this.formError = message;
            this.fieldErrors = field ? [{ field, message }] : [];
        },
        clearErrors() {
            this.formError = "";
            this.fieldErrors = [];
        },
        fieldLabel(field) {
            const labels = {
                phone: "联系电话",
                smsCode: "短信验证码",
                provinceCode: "服务地区",
                cityCode: "服务地区",
                districtCode: "服务地区",
                professionalIntro: "专业简介",
                personalPhoto: "本人真实照片",
                "styles.samples": "原创样片",
                "equipments.proofImages": "设备证明图",
                "certificates.file": "证书原件",
            };
            if (labels[field]) return labels[field];
            const styleMatch = /^styles\[(\d+)\]\.(themeStyleId|samples)$/.exec(field);
            if (styleMatch) {
                const fieldName = { themeStyleId: "摄影主题和具体风格", samples: "原创样片" }[styleMatch[2]];
                return `风格 ${Number(styleMatch[1]) + 1}·${fieldName}`;
            }
            const equipmentMatch = /^equipments\[(\d+)\]/.exec(field);
            if (equipmentMatch) return `设备 ${Number(equipmentMatch[1]) + 1}`;
            const certificateMatch = /^certificates\[(\d+)\]/.exec(field);
            if (certificateMatch) return `证书 ${Number(certificateMatch[1]) + 1}`;
            return "申请资料";
        },
        isImageFile(file) {
            return !!file && IMAGE_TYPES.indexOf(file.type) !== -1;
        },
        isPdfFile(file) {
            return !!file && file.type === "application/pdf";
        },
        validateSelectedFile(file, fileType) {
            if (!file) return "请选择文件";
            if (fileType === "image" && !this.isImageFile(file)) return "图片仅接受 JPG 或 PNG 格式";
            if (fileType === "certificate" && !this.isImageFile(file) && !this.isPdfFile(file)) return "证书仅接受 JPG、PNG 或 PDF 格式";
            if (this.isImageFile(file) && file.size > MAX_IMAGE_SIZE) return "图片文件不能超过 5 MiB";
            return "";
        },
        readFiles(event) {
            const files = Array.prototype.slice.call((event.target && event.target.files) || []);
            if (event.target) event.target.value = "";
            return files;
        },
        selectSingleFile(field, event, fileType) {
            const file = this.readFiles(event)[0];
            const error = this.validateSelectedFile(file, fileType);
            if (error) {
                this.setClientError(error, field);
                return;
            }
            this.form[field] = file;
            this.clearErrors();
        },
        removeSingleFile(field) {
            this.form[field] = null;
            this.clearErrors();
        },
        selectStyleSamples(style, event) {
            const file = this.readFiles(event)[0];
            if (!file) return;
            if (style.samples.length >= 5) {
                this.setClientError("每个摄影风格与样片最多只能上传 5 张原创样片", "styles.samples");
                return;
            }
            const error = this.validateSelectedFile(file, "image");
            if (error) {
                this.setClientError(error, "styles.samples");
                return;
            }
            style.samples = appendUploadFile(style.samples, file, 5);
            style.samplePreviewUrls.push(URL.createObjectURL(file));
            this.clearErrors();
        },
        removeStyleSample(style, index) {
            URL.revokeObjectURL(style.samplePreviewUrls[index]);
            style.samplePreviewUrls.splice(index, 1);
            style.samples = removeUploadFile(style.samples, index);
            this.clearErrors();
        },
        revokeStyleSamplePreviews(style) {
            (style.samplePreviewUrls || []).forEach((url) => URL.revokeObjectURL(url));
        },
        revokeAllSamplePreviews() {
            (this.form.styles || []).forEach(this.revokeStyleSamplePreviews);
            (this.form.equipments || []).forEach(this.revokeEquipmentProofPreviews);
        },
        selectEquipmentProof(equipment, event) {
            const file = this.readFiles(event)[0];
            if (!file) return;
            if (equipment.proofImages.length >= MAX_EQUIPMENT_PROOF_COUNT) {
                this.setClientError("每台设备只能上传 1 张证明图", "equipments.proofImages");
                return;
            }
            const error = this.validateSelectedFile(file, "image");
            if (error) {
                this.setClientError(error, "equipments.proofImages");
                return;
            }
            equipment.proofImages = appendUploadFile(equipment.proofImages, file, MAX_EQUIPMENT_PROOF_COUNT);
            equipment.proofPreviewUrls.push(URL.createObjectURL(file));
            this.clearErrors();
        },
        removeEquipmentProof(equipment, index) {
            URL.revokeObjectURL(equipment.proofPreviewUrls[index]);
            equipment.proofPreviewUrls.splice(index, 1);
            equipment.proofImages = removeUploadFile(equipment.proofImages, index);
            this.clearErrors();
        },
        revokeEquipmentProofPreviews(equipment) {
            (equipment.proofPreviewUrls || []).forEach((url) => URL.revokeObjectURL(url));
        },
        selectCertificateFile(certificate, event) {
            const file = this.readFiles(event)[0];
            const error = this.validateSelectedFile(file, "certificate");
            if (error) {
                this.setClientError(error, "certificates.file");
                return;
            }
            certificate.file = file;
            this.clearErrors();
        },
        addStyle() {
            this.form.styles.push(createStyle());
        },
        removeStyle(index) {
            if (this.form.styles.length > 1) {
                this.revokeStyleSamplePreviews(this.form.styles[index]);
                this.form.styles.splice(index, 1);
            }
        },
        addEquipment() {
            this.form.equipments.push(createEquipment());
        },
        removeEquipment(index) {
            this.revokeEquipmentProofPreviews(this.form.equipments[index]);
            this.form.equipments.splice(index, 1);
        },
        addCertificate() {
            this.form.certificates.push(createCertificate());
        },
        removeCertificate(index) {
            this.form.certificates.splice(index, 1);
        },
        requestSmsCode() {
            const context = this.applicationContext();
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) {
                this.setClientError("请先填写有效的中国大陆手机号", "phone");
                return;
            }
            if (this.smsSending || this.smsCooldown > 0) return;
            this.smsSending = true;
            this.clearErrors();
            return request.post(API.creatorCenter.smsCode, qs.stringify({ phone: this.form.phone, professionalType: 1 })).then((response) => {
                if (!this.applicationIsCurrent(context)) return;
                if (!isResultSuccess(response)) throw response;
                const smsCode = developmentSmsCode.extractDevelopmentSmsCode(response.data);
                if (smsCode) this.form.smsCode = smsCode;
                this.smsCooldown = 60;
                this.smsTimer = window.setInterval(() => {
                    this.smsCooldown -= 1;
                    if (this.smsCooldown <= 0) this.stopSmsTimer();
                }, 1000);
                this.$message.success(getResultMessage(response, "验证码已发送"));
            }).catch((error) => {
                if (this.applicationIsCurrent(context)) this.setClientError(getResultMessage(error, "验证码发送失败，请稍后重试"), "smsCode");
            }).finally(() => {
                if (this.applicationIsCurrent(context)) this.smsSending = false;
            });
        },
        stopSmsTimer() {
            if (this.smsTimer) window.clearInterval(this.smsTimer);
            this.smsTimer = null;
            this.smsCooldown = 0;
        },
        selectedStyleIsValid(style) {
            return this.styleOptionsFor(style).some((item) => String(item.themeStyleId) === String(style.themeStyleId));
        },
        styleCombinationKey(style) {
            return String(style.themeStyleId);
        },
        getFilledEquipments() {
            return this.form.equipments.filter((item) => item.equipmentType || item.brandModel || item.proofImages.length);
        },
        getFilledCertificates() {
            return this.form.certificates.filter((item) => item.name || item.file);
        },
        validateForm() {
            const errors = [];
            const regionSelected = this.selectedProvince && this.selectedCity && this.selectedDistrict;
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) errors.push({ field: "phone", message: "请填写有效的中国大陆手机号" });
            if (!/^\d{6}$/.test(this.form.smsCode)) errors.push({ field: "smsCode", message: "请填写 6 位登录短信验证码" });
            if (!regionSelected) errors.push({ field: "provinceCode", message: "请选择完整的省、市、区／县" });
            if (this.professionalIntroLength < 1 || this.professionalIntroLength > 2000) errors.push({ field: "professionalIntro", message: "专业简介长度须为 1–2000 字" });
            if (!this.form.personalPhoto) errors.push({ field: "personalPhoto", message: "请上传本人真实照片" });
            const selectedStyleIndexes = new Map();
            this.form.styles.forEach((style, index) => {
                if (!style.themeId || !this.selectedStyleIsValid(style)) errors.push({ field: `styles[${index}].themeStyleId`, message: "请选择有效的摄影主题和具体风格" });
                if (style.themeStyleId) {
                    const key = this.styleCombinationKey(style);
                    if (selectedStyleIndexes.has(key)) {
                        errors.push({ field: `styles[${index}].themeStyleId`, message: "同一次申请中不能重复选择相同的主题和风格组合" });
                    } else {
                        selectedStyleIndexes.set(key, index);
                    }
                }
                if (style.samples.length !== 5) errors.push({ field: `styles[${index}].samples`, message: "每个风格必须恰好上传 5 张原创样片" });
            });
            this.getFilledEquipments().forEach((equipment, index) => {
                if (!equipment.equipmentType || !equipment.brandModel) errors.push({ field: `equipments[${index}]`, message: "已填写设备时需完整填写设备类型和品牌型号" });
                if (!equipment.proofImages.length) errors.push({ field: `equipments[${index}].proofImages`, message: "已填写设备时至少上传一张证明图" });
            });
            this.getFilledCertificates().forEach((certificate, index) => {
                if (!certificate.name || !certificate.file) errors.push({ field: `certificates[${index}]`, message: "已填写证书时需同时提供名称和原件" });
            });
            this.fieldErrors = errors;
            this.formError = errors.length ? "请完善以下内容后再提交" : "";
            return errors.length === 0;
        },
        buildFormData() {
            const formData = new FormData();
            formData.append("phone", this.form.phone);
            formData.append("smsCode", this.form.smsCode);
            formData.append("provinceCode", this.selectedProvince.value);
            formData.append("cityCode", this.selectedCity.value);
            formData.append("districtCode", this.selectedDistrict.value);
            formData.append("provinceName", this.selectedProvince.label);
            formData.append("cityName", this.selectedCity.label);
            formData.append("districtName", this.selectedDistrict.label);
            formData.append("professionalIntro", this.form.professionalIntro.trim());
            formData.append("personalPhoto", this.form.personalPhoto);
            this.appendApplicationAgreement(formData);
            this.form.styles.forEach((style, index) => {
                formData.append(`styles[${index}].themeStyleId`, style.themeStyleId);
                style.samples.forEach((file) => formData.append(`styles[${index}].samples`, file));
            });
            this.getFilledEquipments().forEach((equipment, index) => {
                formData.append(`equipments[${index}].equipmentType`, equipment.equipmentType);
                formData.append(`equipments[${index}].brandModel`, equipment.brandModel);
                equipment.proofImages.forEach((file) => formData.append(`equipments[${index}].proofImages`, file));
            });
            this.getFilledCertificates().forEach((certificate, index) => {
                formData.append(`certificates[${index}].name`, certificate.name);
                formData.append(`certificates[${index}].file`, certificate.file);
            });
            return formData;
        },
        errorPayload(error) {
            if (error && error.response && error.response.data) return error.response.data;
            if (error && error.data) return error.data;
            return getResultPayload(error);
        },
        applySubmitError(error) {
            const payload = this.errorPayload(error) || {};
            const status = Number(payload.code || (error && error.response && error.response.status));
            const data = payload.data || {};
            this.fieldErrors = Array.isArray(data.fieldErrors) ? data.fieldErrors.map((item) => ({
                field: asText(item.field) || "表单字段",
                message: asText(item.message) || "字段校验失败",
            })) : [];
            const message = asText(payload.message) || getResultMessage(error, "申请提交失败，请稍后重试");
            this.formError = status === 409
                ? `当前无法提交申请：${message}`
                : message;
        },
        submitApplication() {
            if (this.submitting || !this.checkApplicationAgreement()) return;
            const context = this.applicationContext();
            if (this.dataLoading || this.loadError || this.submitting || !this.validateForm()) return;
            this.submitting = true;
            this.clearErrors();
            return request.post(API.creatorCenter.photographer.applications, this.buildFormData())
                .then((response) => {
                    if (!this.applicationIsCurrent(context)) return;
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success(getResultMessage(response, "摄影师申请已提交，等待审核"));
                    this.$emit("submitted");
                    this.$emit("close");
                })
                .catch((error) => {
                    if (!this.applicationIsCurrent(context)) return;
                    this.applySubmitError(error);
                    this.recoverAgreementConflict(error);
                })
                .finally(() => {
                    if (this.applicationIsCurrent(context)) this.submitting = false;
                });
        },
    },
};
</script>

<style scoped>
.photographer-application-overlay { position: fixed; z-index: 60; inset: 0; display: grid; place-items: center; padding: 28px; background: rgba(32, 27, 41, .56); overscroll-behavior: none; touch-action: pan-y pinch-zoom; }
.photographer-application-dialog { display: flex; width: min(1040px, calc(100vw - 56px)); max-height: min(860px, calc(100vh - 56px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 22px; box-shadow: 0 28px 90px rgba(30, 42, 58, .35); }
.photographer-application-header { display: flex; flex: 0 0 auto; align-items: flex-start; justify-content: space-between; gap: 24px; padding: 22px 28px 18px; border-bottom: 1px solid #e7eff3; }
.photographer-application-header p { margin: 0 0 6px; color: #4d95b9; font-size: 10px; font-weight: 800; letter-spacing: .13em; }.photographer-application-header h2 { margin: 0; color: #3f3158; font-size: 26px; font-weight: 800; }.photographer-application-header span { display: block; margin-top: 6px; color: #7c7483; font-size: 12px; }
.photographer-dialog-close { display: grid; width: 34px; height: 34px; flex: 0 0 auto; place-items: center; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 23px; }
.photographer-application-form { display: flex; min-height: 0; flex: 1; flex-direction: column; }.photographer-application-main { min-height: 0; padding: 24px 28px; overflow-y: auto; overscroll-behavior: contain; }.photographer-loading-copy, .photographer-empty-copy { margin: 0 0 20px; padding: 10px 12px; color: #567a8e; background: #f0f8fc; border-radius: 10px; font-size: 12px; }
.photographer-form-section { padding: 0 0 24px; margin-bottom: 24px; border-bottom: 1px solid #edf1f3; }.photographer-form-section:last-child { padding-bottom: 0; margin-bottom: 0; border-bottom: 0; }.photographer-section-heading { display: flex; gap: 11px; align-items: flex-start; margin-bottom: 16px; }.photographer-section-heading > span, .photographer-section-number-copy > span { display: grid; width: 26px; height: 26px; flex: 0 0 auto; place-items: center; color: #4585a7; background: #e3f4fc; border-radius: 8px; font-size: 10px; font-weight: 900; }.photographer-section-heading h3 { margin: 1px 0 2px; color: #4a4156; font-size: 15px; }.photographer-section-heading h3 small { color: #938a98; font-size: 11px; font-weight: 500; }.photographer-section-heading p { margin: 0; color: #897f8d; font-size: 11px; line-height: 1.6; }.photographer-section-heading--action { justify-content: space-between; }.photographer-section-number-copy { display: flex; gap: 11px; }
.photographer-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 13px; }.photographer-form-grid > label, .photographer-repeat-card > label { display: grid; gap: 6px; color: #5c5563; font-size: 12px; font-weight: 700; }.photographer-form-grid input, .photographer-form-grid select, .photographer-form-grid textarea, .photographer-repeat-card input { width: 100%; padding: 9px 10px; color: #332d49; background: #fff; border: 1px solid #d6d2d8; border-radius: 8px; font: inherit; font-size: 12px; }.photographer-form-grid select:disabled { color: #aaa3ae; background: #f5f5f7; }.photographer-form-grid textarea { min-height: 82px; resize: vertical; }.photographer-form-grid small { margin-top: -3px; color: #9b939f; font-size: 10px; font-weight: 500; text-align: right; }.photographer-field-full { grid-column: 1 / -1; }.photographer-form-grid b, .photographer-attachment-field b, .photographer-sample-heading b { color: #dc657f; }
.photographer-theme-field { display: grid; gap: 6px; color: #5c5563; font-size: 12px; font-weight: 700; }.photographer-theme-field b { color: #dc657f; }.photographer-theme-selector { position: relative; }.photographer-theme-trigger { display: flex; width: 100%; min-height: 38px; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 11px; color: #332d49; background: #fff; border: 1px solid #c5dce7; border-radius: 11px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; transition: border-color 160ms ease; }.photographer-theme-trigger:hover, .photographer-theme-trigger[aria-expanded="true"] { border-color: #6fa9c3; }.photographer-theme-trigger:disabled { cursor: not-allowed; color: #aaa3ae; background: #f5f5f7; border-color: #d9d5dc; }.photographer-theme-trigger i { color: #6692a8; font-size: 16px; font-style: normal; line-height: 1; }.photographer-theme-menu { position: absolute; z-index: 6; top: calc(100% + 7px); right: 0; left: 0; max-height: 216px; padding: 6px; overflow-y: auto; overscroll-behavior: contain; background: #fff; border: 1px solid #bfdae6; border-radius: 13px; }.photographer-theme-menu button { display: flex; width: 100%; min-height: 34px; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 9px; color: #4e5962; background: transparent; border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; }.photographer-theme-menu button:hover { color: #3e7896; background: #eff8fc; }.photographer-theme-menu button.active { color: #326d8a; background: #e4f4fb; font-weight: 400; }.photographer-theme-menu button i { color: #4c93b4; font-style: normal; }
.photographer-code-input { display: flex; gap: 6px; }.photographer-code-input button { flex: 0 0 auto; padding: 0 9px; color: #4782a0; background: #f0f8fc; border: 1px solid #cde5ef; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.photographer-code-input button:disabled { cursor: not-allowed; opacity: .6; }
.photographer-attachment-grid { display: grid; grid-template-columns: minmax(0, 360px); gap: 10px; }.photographer-attachment-field { display: grid; gap: 5px; min-height: 76px; padding: 10px; color: #5e5763; background: #fbfdfe; border: 1px dashed #bad5e2; border-radius: 10px; cursor: pointer; }.photographer-attachment-field span { font-size: 11px; font-weight: 800; }.photographer-attachment-field strong { overflow: hidden; color: #4681a0; font-size: 10px; font-weight: 600; line-height: 1.5; text-overflow: ellipsis; white-space: nowrap; }.photographer-attachment-field em { color: #9a929d; font-size: 10px; font-style: normal; }.photographer-attachment-field input, .photographer-sample-upload-panel input { display: none; }
.photographer-agreement-copy { margin: 11px 0 0; color: #7e7682; font-size: 11px; line-height: 1.6; }.photographer-agreement-copy a { margin: 0 2px; color: #447f9d; font-weight: 700; text-decoration: none; }.photographer-agreement-copy a:hover { text-decoration: underline; }
.photographer-add-button { padding: 6px 9px; color: #467f9d; background: #fff; border: 1px solid #bbdce9; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.photographer-add-button:hover { color: #fff; background: #5793b1; border-color: #5793b1; }.photographer-repeat-card { position: relative; margin-top: 12px; padding: 13px; background: #fbfcfd; border: 1px solid #e5edf1; border-radius: 12px; }.photographer-repeat-card-head, .photographer-subheading { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }.photographer-repeat-card-head strong, .photographer-subheading strong { color: #52495b; font-size: 12px; }.photographer-repeat-card-head button { padding: 0; color: #a26974; background: transparent; border: 0; cursor: pointer; font: inherit; font-size: 10px; }.photographer-style-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }.photographer-sample-heading { display: flex; justify-content: space-between; margin-top: 14px; color: #5c5563; font-size: 11px; }.photographer-sample-upload-panel { display: block; margin-top: 8px; padding: 12px; background: #f8fafb; border: 1px dashed #bad8e6; border-radius: 11px; cursor: pointer; }.photographer-sample-upload-panel.is-complete { cursor: default; }.photographer-sample-upload-box { display: flex; min-height: 98px; align-items: center; justify-content: center; gap: 10px; padding: 12px; background: #fbfcfd; border: 1px dashed #bad8e6; border-radius: 10px; text-align: center; }.photographer-sample-upload-box > i { display: grid; width: 30px; height: 30px; flex: 0 0 auto; place-items: center; color: #4f90ae; background: #fff; border: 1px solid #bad8e6; border-radius: 8px; font-size: 22px; font-style: normal; font-weight: 400; line-height: 1; }.photographer-sample-upload-box > span { display: grid; gap: 2px; }.photographer-sample-upload-box strong { color: #3d7f9d; font-size: 13px; font-weight: 700; }.photographer-sample-upload-box strong.valid { color: #438262; }.photographer-sample-upload-box em { color: #857f76; font-size: 11px; font-style: normal; font-weight: 400; line-height: 1.6; }.photographer-sample-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.photographer-sample-preview { position: relative; display: grid; width: 92px; gap: 4px; overflow: hidden; }.photographer-sample-preview img { display: block; width: 92px; height: 72px; object-fit: cover; border-radius: 7px; }.photographer-sample-preview span { overflow: hidden; color: #678299; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }.photographer-sample-preview button { position: absolute; top: 3px; right: 3px; display: grid; width: 20px; height: 20px; place-items: center; padding: 0; color: #fff; background: rgba(43, 50, 57, .72); border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 16px; line-height: 1; }.photographer-dual-section { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }.photographer-repeat-card--compact { display: grid; gap: 9px; }.photographer-repeat-card--compact .photographer-attachment-field { min-height: 61px; }.photographer-remove-circle { position: absolute; top: 7px; right: 7px; display: grid; width: 20px; height: 20px; place-items: center; color: #8f7e88; background: #f2edf0; border: 0; border-radius: 50%; cursor: pointer; font: inherit; }.photographer-form-error { margin: 0; padding: 10px 12px; color: #a1495c; background: #fff1f3; border-radius: 8px; font-size: 12px; line-height: 1.6; }.photographer-form-error p { margin: 0; font-weight: 700; }.photographer-form-error ul { margin: 6px 0 0; padding-left: 18px; }.photographer-form-error strong { font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 11px; }
.photographer-dialog-footer { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 16px; padding: 15px 28px; color: #8a818d; background: #fff; border-top: 1px solid #e7eff3; font-size: 11px; }.photographer-dialog-footer > span { max-width: 56%; line-height: 1.6; }.photographer-dialog-footer > div { display: flex; gap: 10px; }.photographer-secondary-button, .photographer-submit-button { min-height: 38px; padding: 8px 14px; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.photographer-secondary-button { color: #6b6370; background: #fff; border: 1px solid #d7d2d9; }.photographer-submit-button { color: #fff; background: #568eac; border: 1px solid #568eac; }.photographer-submit-button:disabled, .photographer-secondary-button:disabled { cursor: not-allowed; opacity: .6; }
@media (max-width: 760px) { .photographer-application-overlay { padding: 12px; }.photographer-application-dialog { width: 100%; max-height: calc(100vh - 24px); border-radius: 16px; }.photographer-application-header, .photographer-application-main, .photographer-dialog-footer { padding-right: 16px; padding-left: 16px; }.photographer-attachment-grid, .photographer-form-grid, .photographer-dual-section { grid-template-columns: 1fr; }.photographer-field-full { grid-column: auto; }.photographer-dialog-footer { align-items: flex-start; flex-direction: column; }.photographer-dialog-footer > span { max-width: none; } }
.photographer-form-grid input::placeholder,
.photographer-form-grid textarea::placeholder,
.photographer-repeat-card input::placeholder {
    color: #a8a3ad;
    font-weight: 400;
    opacity: 1;
}

.photographer-form-grid input,
.photographer-form-grid textarea,
.photographer-repeat-card input {
    font-weight: 400;
}

.photographer-theme-trigger.is-placeholder {
    color: #a8a3ad;
    font-weight: 400;
}

.photographer-application-dialog button:focus-visible,
.photographer-application-dialog input:focus-visible,
.photographer-application-dialog textarea:focus-visible {
    outline: 0 !important;
    outline-offset: 0;
    box-shadow: none !important;
}

.photographer-dialog-close {
    line-height: 1 !important;
    text-align: center;
}

/* #88: reuse the application form, adapting the supplied phone design to desktop. */
.photographer-application-dialog { color: #1c1c1e; border-color: #efedf5; border-radius: 24px; }
.photographer-application-header p { color: #7c6ff0; font-size: 10px; letter-spacing: .16em; }
.photographer-application-header h2 { color: #1c1c1e; font-size: 24px; }
.photographer-application-header { border-bottom-color: #efedf5; }
.photographer-section-heading h3 { color: #1c1c1e; }
.photographer-section-heading > span, .photographer-section-number-copy > span { display: none; }
.photographer-submit-button { background: #7c6ff0; border-color: #7c6ff0; min-height: 48px; border-radius: 12px; }
.photographer-dialog-close, .photographer-add-button { color: #7c6ff0; background: #fff; border-color: #ded9ff; }
.photographer-application-dialog button:focus-visible, .photographer-application-dialog input:focus-visible, .photographer-application-dialog textarea:focus-visible { outline: 3px solid #5444cc !important; outline-offset: 2px; }
@media (max-width:760px) {
 .photographer-application-header h2 { font-size: 20px; }
 .photographer-application-main { padding: 24px 16px; }
 .photographer-form-grid input, .photographer-form-grid textarea { font-size: 14px; min-height: 44px; }
 .photographer-dialog-footer > span { display: none; }
 .photographer-dialog-footer > div { width: 100%; }
 .photographer-submit-button { flex: 1; }
}

.photographer-application-dialog ::v-deep .role-portrait-upload__box,
.photographer-application-dialog ::v-deep .professional-image-upload__tile--add,
.photographer-application-dialog ::v-deep .professional-application-file-field__picker { color: #7c6ff0; background: #f6f4ff; border-color: #d1caff; }
.photographer-application-dialog ::v-deep .role-portrait-upload__placeholder i,
.photographer-application-dialog ::v-deep .professional-image-upload__add-content i { width: 26px; height: 26px; color: transparent; font-size: 0; border: 0; border-radius: 0; background: url('../../assets/images/application/bfd8e.svg') center no-repeat; }
.photographer-application-dialog ::v-deep .role-portrait-upload__placeholder strong,
.photographer-application-dialog ::v-deep .professional-image-upload__add-content strong { color: #7c6ff0; }
.photographer-add-button { display: inline-flex; align-items: center; gap: 4px; min-height: 36px; }
.photographer-add-button::before { content: ''; width: 12px; height: 12px; background: url('../../assets/images/application/90dc9.svg') center no-repeat; }
.photographer-code-input button { color: #6254cf; border-color: #ded9ff; background: #f1eeff; }
.photographer-theme-trigger { border-color: #e6e4ee; }
.photographer-theme-trigger > i { width: 15px; height: 15px; color: transparent; font-size: 0; background: url('../../assets/images/application/dd1a5.svg') center no-repeat; }

.photographer-section-heading h3 { font-weight: 700; }
.photographer-add-button { flex-shrink: 0; white-space: nowrap; }
@media(max-width:760px) { .photographer-application-dialog ::v-deep .professional-image-upload__grid { grid-template-columns: repeat(3,minmax(0,1fr)); } }
</style>
